import { Paper, Button, Box } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  BroadcastV2ComposeEmailForm,
  type BroadcastV2TestEmailPayloadFields,
} from "./BroadcastV2ComposeEmailForm";
import { BroadcastV2FrameSelectionPanel } from "./BroadcastV2FrameSelectionPanel";
import { BroadcastWarmupBanner } from "./BroadcastWarmupBanner";
import type { SelectAudienceOption } from "@/features/BroadcastV2/components/SelectAudienceField";
import type { CreateBroadcastRequestV2 } from "@/features/BroadcastV2/types";
import type { Frame } from "@/types/dragon-tiles";

import {
  broadcastV2Keys,
  createBroadcastV2ViaAudiencesApi,
  updateBroadcastStatusViaAudiencesApi,
} from "@/api/broadcastV2Audiences";
import { frameQueryOptions } from "@/api/dragon-tiles";
import { AbDuplicateGuardModal } from "@/features/BroadcastV2/components/AbDuplicateGuardModal";
import { RightColumnVariationTabs } from "@/features/BroadcastV2/components/RightColumnVariationTabs";
import {
  broadcastV2ComposeEmailDraftFormSchemaV2,
  buildCreateEmailBroadcastRequestV2,
  buildDraftEmailBroadcastRequestV2,
} from "@/features/BroadcastV2/createBroadcastPayload";
import { calendarSourceStamp } from "@/features/BroadcastV2/hooks/useCalendarOwnedLock";
import { useVariationDuplicateGuard } from "@/features/BroadcastV2/hooks/useVariationDuplicateGuard";
import {
  BroadcastComposeProvider,
  useBroadcastComposeActions,
  useBroadcastComposeStore,
  useBroadcastComposeStoreApi,
  useBroadcastComposeStoreShallow,
  useIsAbDurationInvalid,
} from "@/features/BroadcastV2/store/broadcastComposeStore";
import { defaultScheduleWindowSendAt } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import {
  clearComposeStore,
  newEmailComposeDraftKey,
} from "@/features/BroadcastV2/store/broadcastComposeStoreRegistry";
import { makeEmailVariation } from "@/features/BroadcastV2/store/variationTypes";
import {
  FrameEditorWrapper,
  type FrameEditorHandle,
} from "@/features/FrameEditor/FrameEditorWrapper";
import { ComposerLayout } from "@/features/TextComposerV2/compose/layout.styles";
import { OrionBackButton } from "@/orion/OrionComponents/OrionBackButton";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

interface BroadcastV2ComposeEmailPageProps {
  siteId: string;
  defaultFromName?: string;
  defaultFromEmail?: string;
  defaultReplyTo?: string;
  timezone?: string;
  /** Frame to auto-select on mount (returning from the tile editor). */
  pendingFrameId?: string;
  /** Set by the Marketing Calendar plan wizard; save returns there. */
  returnTo?: "marketing-calendar";
  /** Audiences to pre-select on a fresh draft (the plan wizard's campaign
   * audiences). Ignored when an in-progress draft is being resumed. */
  initialAudiences?: SelectAudienceOption[];
  /** Campaign date span ("YYYY-MM-DD") constraining scheduling in the plan
   * flow. Ignored when an in-progress draft is being resumed. */
  scheduleWindow?: { startDate: string; endDate: string } | null;
}

export function BroadcastV2ComposeEmailPage({
  siteId,
  defaultFromName,
  defaultFromEmail,
  defaultReplyTo,
  timezone,
  pendingFrameId,
  returnTo,
  initialAudiences,
  scheduleWindow,
}: BroadcastV2ComposeEmailPageProps) {
  return (
    <BroadcastComposeProvider
      // Keep the draft alive across the tile-editor round trip (FOG-1335);
      // cleared on successful create and on a fresh "New Broadcast" entry.
      draftKey={newEmailComposeDraftKey(siteId)}
      initialState={{
        variations: [
          makeEmailVariation({
            fromName: defaultFromName ?? "",
            fromEmail: defaultFromEmail ?? "",
            replyTo: defaultReplyTo ?? "",
          }),
        ],
        ...(initialAudiences?.length
          ? {
              audienceIds: initialAudiences.map((audience) => audience.id),
              audienceOptions: initialAudiences,
            }
          : {}),
        ...(scheduleWindow
          ? {
              scheduleWindow,
              // Prefill the campaign's first plannable send time so a plan
              // draft can never save with an empty schedule.
              scheduledAtIso: defaultScheduleWindowSendAt(
                scheduleWindow,
                timezone,
              ),
            }
          : {}),
      }}
    >
      <BroadcastV2ComposeEmailPageContent
        siteId={siteId}
        timezone={timezone}
        pendingFrameId={pendingFrameId}
        returnTo={returnTo}
      />
    </BroadcastComposeProvider>
  );
}

function BroadcastV2ComposeEmailPageContent({
  siteId,
  timezone,
  pendingFrameId,
  returnTo,
}: {
  siteId: string;
  timezone?: string;
  pendingFrameId?: string;
  /** Set by the Marketing Calendar plan wizard; save returns there. */
  returnTo?: "marketing-calendar";
}) {
  const navigate = useNavigate();
  const { siteId: routeSiteId = "" } = useParams({ strict: false });
  const resolvedSiteId = siteId || routeSiteId;
  const queryClient = useQueryClient();
  const storeApi = useBroadcastComposeStoreApi();
  const actions = useBroadcastComposeActions();

  const sendMode = useBroadcastComposeStore((s) => s.sendMode);
  const scheduledAtIso = useBroadcastComposeStore((s) => s.scheduledAtIso);
  const scheduleWindow = useBroadcastComposeStoreShallow(
    (s) => s.scheduleWindow,
  );
  // Plan drafts must carry a send time: without one the broadcast would come
  // back as an instant send, which the plan flow forbids.
  const missingPlanSendAt = Boolean(scheduleWindow) && !scheduledAtIso;
  const variations = useBroadcastComposeStoreShallow((s) => s.variations);
  const activeVariationIndex = useBroadcastComposeStore(
    (s) => s.activeVariationIndex,
  );
  const abEnabled = variations.length > 1;
  const activeEmail = variations[activeVariationIndex]?.email;
  const activeFrame = activeEmail?.selectedFrame ?? null;

  const { handleFrameSelect, duplicateGuardModal } = useVariationDuplicateGuard(
    {
      siteId: resolvedSiteId,
      abEnabled,
      activeVariationIndex,
      getUsedFrameIdsByOtherVariations: useCallback(() => {
        const state = storeApi.getState();
        return state.variations
          .filter((_, i) => i !== state.activeVariationIndex)
          .map((v) => v.frameId)
          .filter((id): id is string => id != null);
      }, [storeApi]),
      onFrameAccepted: useCallback(
        (index: number, frame: Frame) => {
          // The frame picker hands back a slim list listing whose variants
          // carry no content_text, so seeding it directly leaves the preview
          // blank until a refresh. Fetch the full frame first so the preview
          // renders immediately; fall back to the slim frame if the fetch fails.
          void queryClient
            .fetchQuery(frameQueryOptions(resolvedSiteId, frame.id))
            .then((fullFrame) => actions.setVariationFrame(index, fullFrame))
            .catch(() => actions.setVariationFrame(index, frame));
        },
        [actions, queryClient, resolvedSiteId],
      ),
    },
  );

  // Auto-select the tile the user just created/edited when returning from
  // the tile editor (FOG-1335). Latest-ref so guard-callback identity churn
  // doesn't re-fire the effect.
  const handleFrameSelectRef = useRef(handleFrameSelect);
  handleFrameSelectRef.current = handleFrameSelect;
  const consumedPendingFrameRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingFrameId || consumedPendingFrameRef.current === pendingFrameId) {
      return;
    }
    consumedPendingFrameRef.current = pendingFrameId;

    // Strip the param first (replace) so refresh/back never re-triggers
    // selection and the URL is immediately clean.
    void navigate({
      to: "/dragon-tiles/$siteId/broadcastV2/new/email",
      params: { siteId: resolvedSiteId },
      search: returnTo ? { returnTo } : {},
      replace: true,
    });

    // handleFrameSelect wants a full Frame and only the id survives the
    // round trip; fetch it, then run the A/B duplicate-guard path.
    void queryClient
      .fetchQuery(frameQueryOptions(resolvedSiteId, pendingFrameId))
      .then((frame) => handleFrameSelectRef.current(frame))
      .catch(() => {
        // Frame deleted or fetch failed: leave the selection panel showing.
      });
  }, [pendingFrameId, returnTo, navigate, queryClient, resolvedSiteId]);

  const [saveDraftBusy, setSaveDraftBusy] = useState(false);
  const [activateBusy, setActivateBusy] = useState(false);
  const frameEditorRef = useRef<FrameEditorHandle>(null);
  const isAnyBusy = saveDraftBusy || activateBusy;
  const abDurationInvalid = useIsAbDurationInvalid();

  const createBroadcastMutation = useMutation<
    { id: string },
    Error,
    CreateBroadcastRequestV2
  >({
    mutationKey: ["createBroadcast", resolvedSiteId],
    mutationFn: (payload) =>
      createBroadcastV2ViaAudiencesApi(resolvedSiteId, payload),
    onError: (error: unknown) => {
      enqueueErrorSnackbar(error, "Failed to create broadcast");
    },
  });

  const activateBroadcastMutation = useMutation({
    mutationKey: ["activateBroadcastAfterCreate", resolvedSiteId],
    mutationFn: (broadcastId: string) =>
      updateBroadcastStatusViaAudiencesApi(
        resolvedSiteId,
        broadcastId,
        "BROADCAST_STATUS_ACTIVE",
      ),
    onError: (error: unknown) => {
      enqueueErrorSnackbar(error, "Failed to activate broadcast");
    },
  });

  async function handleSaveDraft() {
    const state = storeApi.getState();
    const parsed = broadcastV2ComposeEmailDraftFormSchemaV2.safeParse({
      name: state.name,
      audienceIds: state.audienceIds,
      sendMode: state.sendMode,
      scheduledAtIso: state.scheduledAtIso,
      variations: state.variations.map((v) => ({
        frameId: v.email?.frameId ?? v.frameId,
      })),
    });
    if (!parsed.success) {
      const first = parsed.error.issues[0]?.message ?? "Check the form";
      enqueueSnackbar(first, { variant: "error" });
      return;
    }

    setSaveDraftBusy(true);
    try {
      if (
        (await frameEditorRef.current?.flushPendingSubjectPreheader()) === false
      )
        return;
      const payload = buildDraftEmailBroadcastRequestV2({
        name: state.name,
        audienceIds: state.audienceIds,
        excludedAudienceIds: state.excludedAudienceIds,
        sendMode: state.sendMode,
        scheduledAtIso: state.scheduledAtIso,
        variations: state.variations,
        abTest: state.abTest,
        smartSend: state.smartSend,
        reachOptimization: state.reachOptimization,
        reachOptimizationPercentage: state.reachOptimizationPercentage,
        enforceFrequencyCap: state.enforceFrequencyCap,
        ...calendarSourceStamp(returnTo),
      });
      const created = await createBroadcastMutation.mutateAsync(payload);
      // The broadcast now exists server-side; drop the ephemeral draft so a
      // later "new" entry starts clean rather than resurrecting this one.
      clearComposeStore(newEmailComposeDraftKey(resolvedSiteId));
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(resolvedSiteId),
      });
      enqueueSnackbar("Saved as draft", { variant: "success" });
      if (returnTo === "marketing-calendar") {
        void navigate({
          to: "/marketing-calendar/$siteId/new",
          params: { siteId: resolvedSiteId },
          search: { addedBroadcastId: created.id },
        });
      } else {
        void navigate({
          to: "/dragon-tiles/$siteId/broadcastV2/$broadcastId/email",
          params: { siteId: resolvedSiteId, broadcastId: created.id },
        });
      }
    } catch {
      // handled by mutation onError
    } finally {
      setSaveDraftBusy(false);
    }
  }

  const flushPendingSubjectPreheader = () =>
    frameEditorRef.current?.flushPendingSubjectPreheader() ??
    Promise.resolve(true);
  const confirmLeaveTile = () =>
    frameEditorRef.current?.confirmLeave() ?? Promise.resolve(true);

  async function handleActivate() {
    const state = storeApi.getState();
    setActivateBusy(true);
    try {
      const payload = buildCreateEmailBroadcastRequestV2({
        name: state.name,
        audienceIds: state.audienceIds,
        excludedAudienceIds: state.excludedAudienceIds,
        sendMode: state.sendMode,
        scheduledAtIso: state.scheduledAtIso,
        variations: state.variations,
        abTest: state.abTest,
        smartSend: state.smartSend,
        reachOptimization: state.reachOptimization,
        reachOptimizationPercentage: state.reachOptimizationPercentage,
        enforceFrequencyCap: state.enforceFrequencyCap,
        ...calendarSourceStamp(returnTo),
      });
      const created = await createBroadcastMutation.mutateAsync(payload);
      // The broadcast now exists server-side (even if activation below
      // fails); drop the ephemeral draft to prevent a duplicate create.
      clearComposeStore(newEmailComposeDraftKey(resolvedSiteId));
      // Instant sends (`schedule: {now: true}`) are created already ACTIVE;
      // only scheduled sends stay DRAFT and need the separate status call.
      if (state.sendMode !== "instant") {
        await activateBroadcastMutation.mutateAsync(created.id);
      }
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(resolvedSiteId),
      });
    } catch {
      // handled by mutation onError
    } finally {
      setActivateBusy(false);
    }
  }

  const testEmailPayloadOverrides: BroadcastV2TestEmailPayloadFields = {
    from_name: activeEmail?.fromName ?? "",
    from_email: activeEmail?.fromEmail ?? "",
    reply_to: activeEmail?.replyTo ?? "",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={1}
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.tokens.outlines.defaultBordersLight}`,
        })}
      >
        <OrionPageHeader
          breadcrumbs={[
            {
              label: "Dragon Tiles",
              to: "/dragon-tiles/$siteId",
              params: { siteId: resolvedSiteId },
            },
            { label: "Broadcast" },
          ]}
          title="Compose Email Broadcast"
          actions={
            <>
              {returnTo === "marketing-calendar" ? (
                <OrionBackButton
                  to="/marketing-calendar/$siteId/new"
                  params={{ siteId: resolvedSiteId }}
                />
              ) : (
                <OrionBackButton
                  to="/dragon-tiles/$siteId/broadcast/list"
                  params={{ siteId: resolvedSiteId }}
                />
              )}
              <Button
                variant={
                  returnTo === "marketing-calendar" ? "contained" : "outlined"
                }
                color="primary"
                disabled={
                  isAnyBusy ||
                  sendMode === "instant" ||
                  abDurationInvalid ||
                  missingPlanSendAt
                }
                onClick={() => void handleSaveDraft()}
              >
                {returnTo === "marketing-calendar"
                  ? "Save and Continue"
                  : "Save as Draft"}
              </Button>
            </>
          }
          paddingY={5}
          paddingX={5}
        />
      </Paper>
      <ComposerLayout>
        <Box
          sx={(theme) => ({
            flex: "1 1 40%",
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            backgroundColor: theme.palette.common.white,
            padding: theme.spacing(6),
            [theme.breakpoints.down("md")]: {
              flex: "1 1 auto",
              width: "100%",
            },
          })}
        >
          <BroadcastWarmupBanner siteId={resolvedSiteId} disableGutters />
          <BroadcastV2ComposeEmailForm
            onActivate={handleActivate}
            flushPendingSubjectPreheader={flushPendingSubjectPreheader}
            flagBlankSubjectLine={() =>
              frameEditorRef.current?.flagBlankSubjectLine()
            }
            confirmLeaveTile={confirmLeaveTile}
            timezone={timezone}
          />
        </Box>
        <Box
          sx={(theme) => ({
            flex: "1 1 60%",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            [theme.breakpoints.down("md")]: {
              flex: "1 1 auto",
              width: "100%",
            },
          })}
        >
          {abEnabled && (
            <RightColumnVariationTabs confirmLeaveTile={confirmLeaveTile} />
          )}
          <Box
            sx={(theme) => ({
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background["paper-elevation-1"],
              // Flat top-left so the leftmost (active) tab connects seamlessly,
              // mirroring the left-column panel.
              borderTopLeftRadius: 0,
            })}
          >
            {activeFrame ? (
              <FrameEditorWrapper
                ref={frameEditorRef}
                frame={activeFrame}
                siteId={resolvedSiteId}
                hideCloseButton
                title={activeFrame.name}
                showReplaceButton
                // Inline saves update the tile; keep this page's copy in step
                // so switching variations and back doesn't show stale copy.
                onFrameSaved={(saved) =>
                  actions.setVariationFrame(activeVariationIndex, saved)
                }
                onReplaceClick={() => {
                  actions.clearVariationFrame(activeVariationIndex);
                }}
                showFullEditorButton
                onFullEditorClick={() => {
                  void navigate({
                    to: "/dragon-tiles/$siteId/$frameId/$variant",
                    params: {
                      siteId: resolvedSiteId,
                      frameId: activeFrame.id,
                      variant: "email",
                    },
                    search: { returnTo: "broadcast-v2" },
                  });
                }}
                testEmailPayloadOverrides={testEmailPayloadOverrides}
              />
            ) : (
              <BroadcastV2FrameSelectionPanel
                siteId={resolvedSiteId}
                selectedFrameId={null}
                onFrameSelect={(frame: Frame) => {
                  handleFrameSelect(frame);
                }}
              />
            )}
          </Box>
        </Box>
      </ComposerLayout>
      <AbDuplicateGuardModal {...duplicateGuardModal} />
    </Box>
  );
}
